import { queryClient } from "@istari-frontend/api-client";
import { sanitizeZipSegment } from "@istari-frontend/folders/core";
import { FileTransferPolicyError } from "@istari-frontend/resource/model";
import { reportAsyncError } from "@istari-frontend/shared";

import { getModelOptions } from "~/api/model/getModelOptions";
import { getArtifactOptions } from "~/api/useModels";
import type { DownloadableFile, ZipDownloadResult } from "~/api/useSystems";
import { SystemResourceType } from "~/enums";
import { assertFileAdmission, assertZipAdmission } from "~/resource/core/file-transfer-policy";
import { resourceQueryOptions } from "~/resource/data-source/api/resource-query-options";
import {
  type BufferedContentMetadata,
  readBufferedContent,
} from "~/resource/data-source/content/read-buffered-content";
import { resolveV2RevisionMetadata } from "~/resource/data-source/content/read-v2-revision-content";
import { fileTransferConfig } from "~/resource/data-source/file-transfer-config";
import { downloadExtensionForMime, revisionDownloadName } from "~/utils/file";

import { buildAndDownloadZipOperation, type FileWithPath } from "./build-and-download-zip";
import { zipOperationQueue } from "./zip-operation-queue";

export function downloadFilesInZip(
  files: DownloadableFile[],
  outputName: string,
  emptyDirs: readonly string[] = [],
): Promise<ZipDownloadResult> {
  return zipOperationQueue.run(() => downloadFilesInZipOperation(files, outputName, emptyDirs));
}

/** Caller must hold zipOperationQueue through collection, saving and cleanup. */
export async function downloadFilesInZipOperation(
  items: DownloadableFile[],
  outputName: string,
  emptyDirs: readonly string[] = [],
): Promise<ZipDownloadResult> {
  if (items.length === 0 && emptyDirs.length === 0) throw new Error("No files provided for download.");
  // Count source entries, not distinct revisions or paths, before any metadata or content work.
  if (items.length > fileTransferConfig.zipMaxFiles) {
    throw new FileTransferPolicyError(
      "zip-too-many-files",
      `ZIP downloads are limited to ${fileTransferConfig.zipMaxFiles} files.`,
    );
  }
  const prepared: PreparedFile[] = [];
  for (const item of items) {
    try {
      const file = await prepareFile(item);
      assertFileAdmission({ name: file.name, size: file.revision.size ?? Number.NaN }, fileTransferConfig.zipMaxBytes);
      if (!file.revision.contentToken) throw missingMetadata(item);
      prepared.push(file);
    } catch (error) {
      reportFileError(error, item, outputName);
    }
  }
  assertZipAdmission(
    prepared.map(({ name, revision }) => ({ name, size: revision.size ?? Number.NaN })),
    fileTransferConfig,
  );

  const files: FileWithPath[] = [];
  for (const entry of prepared) {
    try {
      const content = await readBufferedContent(entry.revision, "zip");
      const file = new File([content], entry.name, { type: entry.mime });
      files.push({ file, zipPath: entry.zipPath });
    } catch (error) {
      reportFileError(error, entry.item, outputName);
    }
  }
  if (files.length === 0 && emptyDirs.length === 0) {
    throw new Error("No files could be processed for download. Please check the file permissions and try again.");
  }
  await buildAndDownloadZipOperation(files, `${outputName}.zip`, emptyDirs);
  return { included: files.length, requested: items.length };
}

type PreparedFile = {
  item: DownloadableFile;
  revision: BufferedContentMetadata;
  name: string;
  mime: string;
  zipPath: string;
};

async function prepareFile(item: DownloadableFile): Promise<PreparedFile> {
  const resourceId = item.modelId || item.resourceId;
  if (!resourceId) throw missingMetadata(item);
  const pinnedId = item.pinnedRevisionId ?? item.revisionId;
  const connected = item.type === SystemResourceType.Connected || item.type === SystemResourceType.ConnectedContainer;
  let revision: BufferedContentMetadata;
  let name = item.extension ? `${item.displayFileName}.${item.extension}` : item.displayFileName;
  let mime = "";
  if (pinnedId) {
    const exact = await queryClient.ensureQueryData(resourceQueryOptions.revision(resourceId, pinnedId));
    if (!exact || exact.fileRevisionId !== pinnedId) throw missingMetadata(item);
    revision = await resolveV2RevisionMetadata(exact);
    mime = exact.mime ?? "";
    if (connected) {
      const parsed = revisionDownloadName(exact);
      const extension = parsed.extension || downloadExtensionForMime(exact.mime);
      const base = parsed.name || item.displayFileName;
      name = extension ? `${base}.${extension}` : base;
    }
  } else {
    if (connected) throw missingMetadata(item);
    const resource =
      item.type === SystemResourceType.Artifact
        ? await queryClient.ensureQueryData(getArtifactOptions(resourceId))
        : await queryClient.ensureQueryData(getModelOptions(resourceId));
    const current = resource.currentRevision();
    if (!current) throw missingMetadata(item);
    revision = await resolveV2RevisionMetadata(current);
    mime = current.mime ?? "";
  }
  name = sanitizeZipSegment(name);
  return { item, revision, name, mime, zipPath: item.folderPath ? `${item.folderPath}/${name}` : name };
}

function missingMetadata(item: DownloadableFile): FileTransferPolicyError {
  return new FileTransferPolicyError(
    "invalid-size",
    `The exact revision size of "${item.displayFileName}" is unavailable.`,
  );
}

function reportFileError(error: unknown, item: DownloadableFile, outputName: string): void {
  if (error instanceof FileTransferPolicyError) return;
  reportAsyncError("zip-download-file", error, {
    resourceId: item.modelId || item.resourceId,
    pinnedRevisionId: item.pinnedRevisionId ?? item.revisionId,
    zipOutputName: outputName,
    displayFileName: item.displayFileName,
  });
}
